/**
 * Authenticator-app 2FA copy (MYN-241): setup, recovery codes, and the
 * recovery-code sign-in, plus the account page's recovery-code card. A
 * standalone namespace rather than part of `ui-strings.auth.ts`, which keeps the
 * shared login-code step's copy (`UI_STRINGS.TWO_FACTOR`).
 */
export const TWO_FACTOR_SETUP_STRINGS = {
  BADGE: 'Two-step verification',
  HEADING_PREFIX: 'Set up your ',
  HEADING_HIGHLIGHT: 'authenticator app',
  SUBTITLE:
    'Every sign-in now needs a code from an authenticator app. Use any app you like, such as Google Authenticator, Microsoft Authenticator or 1Password.',
  STEP_SCAN_TITLE: '1. Scan the QR code',
  STEP_SCAN_SUBTITLE: 'Open your authenticator app, add an account, and scan this code.',
  QR_ALT: 'QR code for your authenticator app',
  MANUAL_LABEL: "Can't scan it? Enter this key instead",
  COPY_KEY: 'Copy key',
  KEY_COPIED: 'Key copied',
  STEP_CODE_TITLE: '2. Enter the 6-digit code',
  STEP_CODE_SUBTITLE: 'Type the code your app shows for My NEXT Raise.',
  CODE_LABEL: 'Authenticator code',
  CODE_FORMAT_ERROR: 'Enter the 6-digit code from your app',
  SUBMIT: 'Verify and continue',
  SUBMITTING: 'Verifying...',
  BACK: 'Back to sign in',
  LOAD_FAILED: 'We could not start the setup. Try again, or sign in again if it keeps failing.',
  QR_FAILED: 'We could not draw the QR code. Enter the key below instead.',
  KEY_COPY_FAILED: 'Copy is not available here. Select the key and type it into your app.',
  SETUP_FAILED: 'We could not verify that code. Please try again.',
  ERROR_CODE_INVALID: 'That code is not right. Check your authenticator app and try again.',
  ERROR_SESSION_EXPIRED: 'Your sign-in has expired. Please sign in again.',
  ERROR_LOCKED: 'Too many incorrect codes. Wait 15 minutes, then try again.',
} as const;

export const RECOVERY_CODES_STRINGS = {
  HEADING: 'Save your recovery codes',
  DESCRIPTION:
    'If you lose your phone, each of these codes lets you sign in once. Store them somewhere safe. They will not be shown again.',
  LIST_LABEL: 'Recovery codes',
  COPY_ALL: 'Copy codes',
  COPIED: 'Codes copied',
  COPY_FAILED: 'Copy is not available here. Download the codes or write them down.',
  DOWNLOAD: 'Download',
  DOWNLOAD_FILENAME: 'my-next-raise-recovery-codes.txt',
  ACKNOWLEDGE: 'I have saved my recovery codes',
  CONTINUE: 'Continue to the platform',
  /** On the Issue Report journey, where the startup step comes next. */
  CONTINUE_JOURNEY: 'Continue to your Startup Profile',
} as const;

export const RECOVERY_SIGN_IN_STRINGS = {
  USE_RECOVERY: 'Use a recovery code instead',
  USE_APP: 'Use your authenticator app instead',
  RECOVERY_LABEL: 'Recovery code',
  RECOVERY_PLACEHOLDER: 'XXXXX-XXXXX',
  RECOVERY_REQUIRED: 'Enter one of your recovery codes',
  RECOVERY_CARD_SUBTITLE:
    'Enter one of the recovery codes you saved when you set up your authenticator app.',
  ERROR_RECOVERY_INVALID: 'That recovery code is not valid or has already been used.',
} as const;

/** Setup after an abandoned authenticator re-scan (FR-221). */
export const SETUP_RECOVERY_GATE_STRINGS = {
  TITLE: 'Confirm it is you',
  SUBTITLE:
    'Your authenticator app was being changed and the change was not finished. Enter one of your recovery codes to set it up again.',
  SUBMIT: 'Continue',
  SUBMITTING: 'Checking...',
  NO_CODES: 'No recovery codes? Contact support to reset two-step verification.',
} as const;

export const ACCOUNT_SECURITY_STRINGS = {
  SECTION_TITLE: 'Two-step verification',
  FIELD_LABEL: 'Recovery codes',
  FIELD_HINT: 'Lost your codes? Create a new set. The old ones stop working.',
  REGENERATE: 'Create new recovery codes',
  REGENERATING: 'Creating...',
  CONFIRM_TITLE: 'Create new recovery codes?',
  CONFIRM_DESCRIPTION:
    'Your current recovery codes stop working as soon as the new ones are created. Make sure you can save the new set before you continue.',
  CONFIRM: 'Create new codes',
  DIALOG_TITLE: 'Your new recovery codes',
  DONE: 'Done',
  NOT_ENROLLED:
    'Two-step verification is set up at your next sign-in. Recovery codes are created then.',
  FAILED: 'We could not create new recovery codes. Please try again.',
  RESCAN_LABEL: 'Authenticator app',
  RESCAN_HINT: 'Changed your email? Scan a new QR code so your app shows your current email.',
  RESCAN: 'Scan a new QR code',
} as const;

/** Moving the authenticator to a new app entry from the account page (FR-220). */
export const RESCAN_STRINGS = {
  TITLE: 'Scan a new QR code',
  // Cognito keeps the current entry working until the new one is verified (A-258, checked 2026-10-27).
  STEP_UP_DESCRIPTION: 'Confirm it is you. Your current app entry keeps working until you finish.',
  PASSWORD_LABEL: 'Current password',
  PASSWORD_REQUIRED: 'Enter your current password',
  CURRENT_CODE_LABEL: 'Code from your current authenticator app',
  NEW_CODE_LABEL: 'Code from the new entry in your app',
  CONTINUE: 'Continue',
  CONTINUING: 'Checking...',
  SCAN_DESCRIPTION:
    'Scan this code with your authenticator app, then type the 6-digit code the new entry shows. Keep the old My NEXT Raise entry until you finish.',
  VERIFY: 'Verify and finish',
  VERIFYING: 'Verifying...',
  CANCEL: 'Cancel',
  CODES_TITLE: 'Your new recovery codes',
  CODES_DESCRIPTION:
    'Your app now shows your current email. Delete the old My NEXT Raise entry from your app: it no longer works, and neither do your old recovery codes.',
  DONE: 'Done',
  ERROR_PASSWORD: 'The current password is incorrect.',
  ERROR_EXPIRED: 'This change took too long. Close this window and start again.',
  FAILED: 'We could not change your authenticator app. Please try again.',
} as const;

export const EMAIL_VERIFIED_SIGN_IN_STRINGS = {
  NOTE: 'One more step: sign in to set up two-step verification with an authenticator app.',
  CTA: 'Sign in to finish setup',
} as const;
